import React from 'react'
import styled from 'styled-components'
import { useStaticQuery, Link, graphql } from 'gatsby'

import Section from '@components/Section'
import Heading, { headingConstants } from '@components/Heading'
import Sticky from '@components/Sticky'
import Image from '@components/Image'

import media from '@styles/media'

const aboutText = [
  `This <strong>space</strong> is intended to become <i>(soon!)</i> the Home of <strong>my Portfolio</strong>, but  there
   is still some work before going out of beta & moving in...`,
  `While <strong>things are moving</strong> under the hood to build a fully fledged Portfolio, you
  already can <strong><a href="/things">take a tour</a></strong> and get a taste of <strong>my work</strong>.`,
  `If you're eager to see more, or just want to chat on an idea, <strong><a href="/contact">drop me a line</a>!</strong>`,
  `Also, I'll be happy to get your feedback!!<span class="note">You can send it from <strong><a href="/contact">here</a></strong> or file an <strong><a href="https://gitlab.com/j.boucharlat/portfolio-website/-/issues" target="_blank">issue</a></strong> on Gitlab.</span>`,
]
const aboutTextTldr = [
  `<strong>Designing</strong> & <strong>Crafting</strong> things was one of my
  earliest hobby, constantly searching ways of <strong>improvement</strong> & <strong>
  simplification</strong>.`,
  `Naturally I ended up doing it <strong>professionally</strong> years ago...`,
  `Sure, this led me to work with/in <strong>various industries</strong> and on all
  kind of <i>"</i><strong>ware</strong><i>"</i>, from <i>"</i><strong>hard</strong><i>"</i> to <i>"</i><strong>soft</strong><i>"</i>, collecting a
  significantly <strong>wide experience</strong> on the way.`,
]

const imageQuery = graphql`
  query ShapeImageQuery {
    glow: file(name: { regex: "/mobile-glow/" }) {
      childImageSharp {
        fluid(maxWidth: 787, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
  }
`

/**
 * <HomeAbout />
 *
 * The challenge was to create a FadeIn and FadeOut effect as the user
 * scrolls over the main Paragraphs of the homepage.
 *
 * To accomplish this we do two things
 * 1. Using a Padding/Margin trick to get the FadeOut to work nicely
 * 2. Overlaying a fixed gradient to get the FadeIn to work nicely
 */
const HomeAbout = () => {
  const { glow } = useStaticQuery(imageQuery)

  return (
    <>
      <MobileContainer>
        <JCard aria-hidden="true" />
        <MediaContainer>
          <Image src={glow.childImageSharp.fluid} />
        </MediaContainer>
      </MobileContainer>
      <Gradient>
        <Grid narrow>
          <Sticky
            top={140}
            height="522px"
            disableOnMobile
            render={() => (
              <AboutHeading>Ongoing building process...</AboutHeading>
            )}
          />
          <div>
            {aboutText.map((text, index) => (
              <TextContainer data-scroll-fade={true} key={index}>
                <Text dangerouslySetInnerHTML={{ __html: text }} />
              </TextContainer>
            ))}
          </div>
        </Grid>
        <Grid narrow>
          <Sticky
            top={140}
            height="566px"
            disableOnMobile
            render={() => <AboutHeading>Me</AboutHeading>}
          />
          <div>
            {aboutTextTldr.map((text, index) => (
              <TextContainer data-scroll-fade={true} key={index}>
                <Text dangerouslySetInnerHTML={{ __html: text }} />
              </TextContainer>
            ))}
          </div>
        </Grid>
      </Gradient>
    </>
  )
}

export default HomeAbout

const Gradient = styled.div`
  position: relative;
  z-index: 1;
  background: #08080b;
  background: linear-gradient(#08080b, #101216);

  ${media.tablet`
    background: linear-gradient(transparent, #101216);
  `};
`

const Grid = styled(Section)`
  position: relative;
  display: grid;
  grid-template-columns: 144px 670px;
  grid-column-gap: 128px;
  padding-top: 100px;
  padding-bottom: 30px;
  z-index: 2;

  ${media.tablet`
    padding-top: 80px;
    display: block;
    padding-bottom: 100;
  `}
`

const TextContainer = styled.div`
  position: relative;
  padding-top: 0;
  margin-bottom: 75px;

  ${media.tablet`
    top: 0;
    margin: 0;
    padding: 0 0 40px 0;
  `};
`

const Text = styled.p`
  font-size: ${headingConstants.h1.fontsize};
  color: ${(p) => p.theme.colors.white};

  a {
    text-decoration: underline;
    transition: color 0.25s ease;
    color: ${(p) => p.theme.colors.white};

    &:hover,
    &:focus {
      color: ${(p) => p.theme.colors.blue};
    }
  }

  span.note {
    display: block;
    color: ${(p) => p.theme.colors.grey};
    font-size: 16px;
    transition: color 0.25s ease;
    a {
      color: ${(p) => p.theme.colors.white60};
      &:hover,
      &:focus {
        color: ${(p) => p.theme.colors.blue60};
      }
    }
  }

  ${media.tablet`
    font-size: ${headingConstants.h1.fontsizeMobile};
    line-height: ${headingConstants.h1.lineheightMobile};
  `};

  & pre {
    opacity: 0.2;
    weight: 700;
  }
`

const TextLink = styled(Link)`
  color: #fff;
  text-decoration: underline;
  transition: color 0.25s ease;

  &:hover,
  &:focus {
    color: ${(p) => p.theme.colors.blue};
  }
`
const AboutHeading = styled(Heading.h2)`
  position: relative;
  color: ${(p) => p.theme.colors.grey};
`

const MobileContainer = styled.div`
  position: relative;
  max-width: 100vw;
  margin-bottom: -85%;
  overflow-x: hidden;
  background: #08070b;
  box-shadow: 0px -40px 40px rgba(8, 7, 11, 0.8);

  ${media.tablet_up`
    display: none;
  `}
`

const MediaContainer = styled.div`
  position: absolute;
  left: 0;
  top: -100px;
  height: 100%;
  width: 100%;
`

const JCard = () => (
  <svg
    width="100%"
    height="900"
    viewBox="0 0 375 1209"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      width="300"
      height="480"
      transform="translate(38 70)"
      fill="url(#paint0_linear)"
      fillOpacity="0.5"
    ></rect>
    <path
      d="M217.8 527.713H214.476V524.471C212.834 526.96 210.96 528.205 208.853 528.205C207.157 528.205 205.857 527.631 204.954 526.482C204.051 525.305 203.6 523.595 203.6 521.351V514.046C203.6 511.885 204.092 510.202 205.077 508.998C206.09 507.794 207.526 507.192 209.387 507.192C210.18 507.192 211.015 507.397 211.89 507.808C212.766 508.191 213.573 508.67 214.312 509.244V499.682H217.8V527.713ZM210.167 510.311C209.182 510.311 208.415 510.667 207.868 511.378C207.348 512.062 207.088 513.116 207.088 514.539V520.9C207.088 523.691 207.95 525.086 209.674 525.086C210.495 525.086 211.302 524.772 212.095 524.142C212.916 523.486 213.655 522.569 214.312 521.392V512.363C213.737 511.761 213.053 511.269 212.26 510.886C211.494 510.503 210.796 510.311 210.167 510.311Z"
      fill="black"
    ></path>
    <path
      d="M232.148 507.192C234.446 507.192 236.197 507.822 237.401 509.08C238.605 510.339 239.207 512.158 239.207 514.539V518.766H228.659V521.187C228.659 522.583 228.987 523.595 229.644 524.224C230.328 524.826 231.395 525.127 232.845 525.127C234.268 525.127 236.047 524.908 238.181 524.471L238.263 527.508C235.718 527.973 233.899 528.205 232.804 528.205C230.096 528.205 228.139 527.617 226.935 526.441C225.759 525.264 225.171 523.445 225.171 520.982V514.539C225.171 512.158 225.745 510.339 226.894 509.08C228.071 507.822 229.822 507.192 232.148 507.192ZM232.148 510.106C231.081 510.106 230.232 510.448 229.603 511.132C228.974 511.789 228.659 512.746 228.659 514.005V515.975H235.718V514.128C235.718 512.815 235.39 511.816 234.733 511.132C234.104 510.448 233.242 510.106 232.148 510.106Z"
      fill="black"
    ></path>
    <path
      d="M252.925 500.338C254.403 500.338 256.332 500.557 258.712 500.995L258.63 504.073C256.386 503.635 254.485 503.416 252.925 503.416C251.338 503.416 250.189 503.731 249.478 504.36C248.794 504.99 248.452 505.947 248.452 507.233V508.3C248.452 508.984 248.534 509.532 248.698 509.942C248.89 510.325 249.218 510.667 249.683 510.968C250.148 511.242 250.846 511.529 251.776 511.83L255.593 513.102C257.426 513.731 258.712 514.566 259.451 515.606C260.217 516.645 260.6 518.164 260.6 520.161V520.941C260.6 523.376 259.889 525.196 258.466 526.399C257.07 527.603 254.772 528.205 251.571 528.205C250.011 528.205 248.014 527.932 245.579 527.384L245.661 524.224C247.932 524.772 249.875 525.045 251.489 525.045C253.541 525.045 254.991 524.744 255.839 524.142C256.687 523.513 257.112 522.542 257.112 521.228V519.874C257.112 518.916 256.893 518.191 256.455 517.699C256.044 517.179 255.306 516.741 254.239 516.385L250.011 514.99C248.124 514.361 246.81 513.554 246.071 512.569C245.333 511.556 244.963 510.038 244.963 508.013V507.562C244.963 505.181 245.593 503.389 246.851 502.185C248.137 500.954 250.162 500.338 252.925 500.338Z"
      fill="black"
    ></path>
    <path
      d="M270.447 507.685V527.713H266.959V507.685H270.447ZM268.683 499.6C269.421 499.6 269.982 499.764 270.365 500.092C270.776 500.42 270.981 500.886 270.981 501.488V502.678C270.981 503.28 270.776 503.745 270.365 504.073C269.982 504.374 269.421 504.525 268.683 504.525C267.971 504.525 267.41 504.374 267 504.073C266.617 503.745 266.425 503.28 266.425 502.678V501.488C266.425 500.886 266.617 500.42 267 500.092C267.41 499.764 267.971 499.6 268.683 499.6Z"
      fill="black"
    ></path>
    <path
      d="M284.796 507.192C286 507.192 287.094 507.37 288.079 507.726L294.481 507.644V510.722L291.116 510.64C291.581 511.625 291.814 512.651 291.814 513.718V514.621C291.814 516.618 291.198 518.232 289.967 519.464C288.736 520.667 287.012 521.269 284.796 521.269C283.373 521.269 282.21 521.133 281.307 520.859C280.705 521.351 280.404 521.912 280.404 522.542V522.788C280.404 523.226 280.541 523.554 280.815 523.773C281.088 523.964 281.622 524.088 282.415 524.142L288.53 524.594C289.789 524.703 290.801 525.182 291.567 526.03C292.361 526.851 292.758 527.891 292.758 529.149V530.668C292.758 531.844 292.525 532.802 292.06 533.541C291.595 534.279 290.733 534.84 289.474 535.223C288.243 535.606 286.465 535.798 284.139 535.798C283.263 535.798 282.224 535.702 281.02 535.511C279.843 535.319 278.722 535.073 277.655 534.772L277.778 531.53C280.322 532.378 282.484 532.802 284.262 532.802C285.74 532.802 286.834 532.706 287.545 532.515C288.284 532.35 288.763 532.091 288.982 531.735C289.228 531.379 289.351 530.846 289.351 530.134V529.806C289.351 529.177 289.173 528.684 288.818 528.328C288.489 527.973 288.011 527.781 287.381 527.754L281.225 527.343C279.912 527.261 278.913 526.892 278.229 526.235C277.545 525.579 277.203 524.689 277.203 523.568V522.747C277.203 522.145 277.354 521.557 277.655 520.982C277.983 520.407 278.448 519.901 279.05 519.464C278.585 518.944 278.243 518.287 278.024 517.494C277.832 516.673 277.737 515.674 277.737 514.498V513.595C277.737 511.652 278.339 510.106 279.542 508.957C280.746 507.78 282.497 507.192 284.796 507.192ZM284.796 510.106C283.647 510.106 282.757 510.407 282.128 511.009C281.526 511.584 281.225 512.418 281.225 513.513V514.867C281.225 515.879 281.307 516.632 281.471 517.124C281.663 517.589 281.991 517.918 282.456 518.109C282.921 518.273 283.619 518.355 284.549 518.355C285.808 518.355 286.752 518.054 287.381 517.453C288.011 516.851 288.325 515.989 288.325 514.867V513.636C288.325 512.487 288.161 511.488 287.833 510.64C287.504 510.503 287.053 510.38 286.478 510.27C285.931 510.161 285.37 510.106 284.796 510.106Z"
      fill="black"
    ></path>
    <path
      d="M308.542 507.192C310.293 507.192 311.633 507.794 312.564 508.998C313.521 510.175 314 511.857 314 514.046V527.713H310.511V514.539C310.511 511.803 309.622 510.434 307.844 510.434C307.05 510.434 306.243 510.735 305.422 511.337C304.602 511.939 303.89 512.76 303.288 513.8V527.713H299.8V507.685H303.083V510.681C303.822 509.559 304.643 508.697 305.546 508.095C306.476 507.493 307.474 507.192 308.542 507.192Z"
      fill="black"
    ></path>
    <g filter="url(#filter2_d)">
      <path
        d="M191.248 337.471C199.665 329.042 203.874 315.849 203.874 297.891V154.965H118.787V193.445H154.468V297.891C154.468 302.106 153.188 305.496 150.626 308.061C148.247 310.443 144.953 311.634 140.745 311.634C125.74 311.634 111.102 311.176 96.8289 310.26V347.366C116.408 349.198 134.889 350.115 152.273 350.115C169.839 350.115 182.831 345.9 191.248 337.471Z"
        stroke="#009CEF"
        strokeOpacity="0.3"
        strokeWidth="1.49758"
      ></path>
      <path
        d="M277.971 306.137H228.566V347.366H277.971V306.137Z"
        stroke="#009CEF"
        strokeOpacity="0.3"
        strokeWidth="1.49758"
      ></path>
    </g>
    <path
      d="M152.272 350.116C132.366 350.116 111.203 348.932 88.7821 346.562"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M88.7821 309.695C106.018 310.988 123.696 311.634 141.816 311.634"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M98.4149 154.967H235.269"
      opacity="0.8"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M98.4149 193.445H235.269"
      opacity="0.8"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M218.419 306.139L291.323 306.139"
      opacity="0.8"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M84.4679 347.367L295.178 347.367"
      opacity="0.8"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M154.468 178.666V311.635"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M118.787 146.732L118.787 205.927"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M203.874 146.732L203.874 356.249"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M96.8287 297.893L96.8287 357.087"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M277.971 297.893L277.971 357.087"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M228.566 296.719L228.566 355.913"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <ellipse
      cx="141.816"
      cy="298.965"
      rx="12.652"
      ry="12.6698"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></ellipse>
    <path
      d="M203.874 297.893C203.874 315.85 199.665 329.044 191.248 337.473C182.831 345.902 169.839 350.116 152.273 350.116M152.272 350.116C146.291 350.116 140.356 349.064 134.732 347.007M203.874 297.892C203.874 290.774 202.436 283.731 199.648 277.195"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <ellipse
      cx="171.424"
      cy="321.441"
      rx="24.6877"
      ry="24.7222"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></ellipse>
    <path
      d="M123.051 284.33L210.676 352.673"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.12319"
    ></path>
    <path
      d="M5.58532 69.9998L384.446 69.9999M17 549.999L361.4 549.999M38 596.2L38 37.5996M338 581.799L338 48.9996"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.5"
    ></path>
    <g opacity="0.7" filter="url(#filter3_f)">
      <mask
        id="mask0"
        mask-type="alpha"
        maskUnits="userSpaceOnUse"
        x="5"
        y="648"
        width="380"
        height="561"
      >
        <rect
          x="5.59998"
          y="648.4"
          width="379.2"
          height="560.4"
          fill="url(#paint1_linear)"
        ></rect>
      </mask>
      <g mask="url(#mask0)">
        <rect
          width="300"
          height="480"
          transform="matrix(1 0 0 -1 38 1174)"
          fill="url(#paint2_linear)"
          fillOpacity="0.5"
        ></rect>
        <path
          d="M217.8 716.287H214.476V719.529C212.834 717.04 210.96 715.795 208.853 715.795C207.157 715.795 205.857 716.369 204.954 717.518C204.051 718.695 203.6 720.405 203.6 722.649V729.954C203.6 732.115 204.092 733.798 205.077 735.002C206.09 736.206 207.526 736.808 209.387 736.808C210.18 736.808 211.015 736.603 211.89 736.192C212.766 735.809 213.573 735.33 214.312 734.756V744.318H217.8V716.287ZM210.167 733.689C209.182 733.689 208.415 733.333 207.868 732.622C207.348 731.938 207.088 730.884 207.088 729.461V723.1C207.088 720.309 207.95 718.914 209.674 718.914C210.495 718.914 211.302 719.228 212.095 719.858C212.916 720.514 213.655 721.431 214.312 722.608V731.637C213.737 732.239 213.053 732.731 212.26 733.114C211.494 733.497 210.796 733.689 210.167 733.689Z"
          fill="black"
        ></path>
        <path
          d="M232.148 736.808C234.446 736.808 236.197 736.178 237.401 734.92C238.605 733.661 239.207 731.842 239.207 729.461V725.234H228.659V722.813C228.659 721.417 228.987 720.405 229.644 719.776C230.328 719.174 231.395 718.873 232.845 718.873C234.268 718.873 236.047 719.092 238.181 719.529L238.263 716.492C235.718 716.027 233.899 715.795 232.804 715.795C230.096 715.795 228.139 716.383 226.935 717.559C225.759 718.736 225.171 720.555 225.171 723.018V729.461C225.171 731.842 225.745 733.661 226.894 734.92C228.071 736.178 229.822 736.808 232.148 736.808ZM232.148 733.894C231.081 733.894 230.232 733.552 229.603 732.868C228.974 732.211 228.659 731.254 228.659 729.995V728.025H235.718V729.872C235.718 731.185 235.39 732.184 234.733 732.868C234.104 733.552 233.242 733.894 232.148 733.894Z"
          fill="black"
        ></path>
        <path
          d="M252.925 743.662C254.403 743.662 256.332 743.443 258.712 743.005L258.63 739.927C256.386 740.365 254.485 740.584 252.925 740.584C251.338 740.584 250.189 740.269 249.478 739.64C248.794 739.01 248.452 738.053 248.452 736.767V735.7C248.452 735.016 248.534 734.468 248.698 734.058C248.89 733.675 249.218 733.333 249.683 733.032C250.148 732.758 250.846 732.471 251.776 732.17L255.593 730.898C257.426 730.269 258.712 729.434 259.451 728.394C260.217 727.355 260.6 725.836 260.6 723.839V723.059C260.6 720.624 259.889 718.804 258.466 717.601C257.07 716.397 254.772 715.795 251.571 715.795C250.011 715.795 248.014 716.068 245.579 716.616L245.661 719.776C247.932 719.228 249.875 718.955 251.489 718.955C253.541 718.955 254.991 719.256 255.839 719.858C256.687 720.487 257.112 721.458 257.112 722.772V724.126C257.112 725.084 256.893 725.809 256.455 726.301C256.044 726.821 255.306 727.259 254.239 727.615L250.011 729.01C248.124 729.639 246.81 730.446 246.071 731.431C245.333 732.444 244.963 733.962 244.963 735.987V736.438C244.963 738.819 245.593 740.611 246.851 741.815C248.137 743.046 250.162 743.662 252.925 743.662Z"
          fill="black"
        ></path>
        <path
          d="M270.447 736.315V716.287H266.959V736.315H270.447ZM268.683 744.4C269.421 744.4 269.982 744.236 270.365 743.908C270.776 743.58 270.981 743.114 270.981 742.512V741.322C270.981 740.72 270.776 740.255 270.365 739.927C269.982 739.626 269.421 739.475 268.683 739.475C267.971 739.475 267.41 739.626 267 739.927C266.617 740.255 266.425 740.72 266.425 741.322V742.512C266.425 743.114 266.617 743.58 267 743.908C267.41 744.236 267.971 744.4 268.683 744.4Z"
          fill="black"
        ></path>
        <path
          d="M284.796 736.808C286 736.808 287.094 736.63 288.079 736.274L294.481 736.356V733.278L291.116 733.36C291.581 732.375 291.814 731.349 291.814 730.282V729.379C291.814 727.382 291.198 725.768 289.967 724.536C288.736 723.333 287.012 722.731 284.796 722.731C283.373 722.731 282.21 722.867 281.307 723.141C280.705 722.649 280.404 722.088 280.404 721.458V721.212C280.404 720.774 280.541 720.446 280.815 720.227C281.088 720.036 281.622 719.912 282.415 719.858L288.53 719.406C289.789 719.297 290.801 718.818 291.567 717.97C292.361 717.149 292.758 716.109 292.758 714.851V713.332C292.758 712.156 292.525 711.198 292.06 710.459C291.595 709.721 290.733 709.16 289.474 708.777C288.243 708.394 286.465 708.202 284.139 708.202C283.263 708.202 282.224 708.298 281.02 708.489C279.843 708.681 278.722 708.927 277.655 709.228L277.778 712.47C280.322 711.622 282.484 711.198 284.262 711.198C285.74 711.198 286.834 711.294 287.545 711.485C288.284 711.65 288.763 711.909 288.982 712.265C289.228 712.621 289.351 713.154 289.351 713.866V714.194C289.351 714.823 289.173 715.316 288.818 715.672C288.489 716.027 288.011 716.219 287.381 716.246L281.225 716.657C279.912 716.739 278.913 717.108 278.229 717.765C277.545 718.421 277.203 719.311 277.203 720.432V721.253C277.203 721.855 277.354 722.443 277.655 723.018C277.983 723.593 278.448 724.099 279.05 724.536C278.585 725.056 278.243 725.713 278.024 726.506C277.832 727.327 277.737 728.326 277.737 729.502V730.405C277.737 732.348 278.339 733.894 279.542 735.043C280.746 736.22 282.497 736.808 284.796 736.808ZM284.796 733.894C283.647 733.894 282.757 733.593 282.128 732.991C281.526 732.416 281.225 731.582 281.225 730.487V729.133C281.225 728.121 281.307 727.368 281.471 726.876C281.663 726.411 281.991 726.082 282.456 725.891C282.921 725.727 283.619 725.645 284.549 725.645C285.808 725.645 286.752 725.946 287.381 726.547C288.011 727.149 288.325 728.011 288.325 729.133V730.364C288.325 731.513 288.161 732.512 287.833 733.36C287.504 733.497 287.053 733.62 286.478 733.73C285.931 733.839 285.37 733.894 284.796 733.894Z"
          fill="black"
        ></path>
        <path
          d="M308.542 736.808C310.293 736.808 311.633 736.206 312.564 735.002C313.521 733.825 314 732.143 314 729.954V716.287H310.511V729.461C310.511 732.197 309.622 733.566 307.844 733.566C307.05 733.566 306.243 733.265 305.422 732.663C304.602 732.061 303.89 731.24 303.288 730.2V716.287H299.8V736.315H303.083V733.319C303.822 734.441 304.643 735.303 305.546 735.905C306.476 736.507 307.474 736.808 308.542 736.808Z"
          fill="black"
        ></path>
        <g filter="url(#filter6_d)">
          <path
            d="M191.248 906.529C199.665 914.958 203.874 928.151 203.874 946.109V1089.04H118.787V1050.55H154.468V946.109C154.468 941.894 153.188 938.504 150.626 935.939C148.247 933.557 144.953 932.366 140.745 932.366C125.74 932.366 111.102 932.824 96.8289 933.74V896.634C116.408 894.802 134.889 893.885 152.273 893.885C169.839 893.885 182.831 898.1 191.248 906.529Z"
            stroke="#009CEF"
            strokeOpacity="0.3"
            strokeWidth="1.49758"
          ></path>
          <path
            d="M277.971 937.863H228.566V896.634H277.971V937.863Z"
            stroke="#009CEF"
            strokeOpacity="0.3"
            strokeWidth="1.49758"
          ></path>
        </g>
        <path
          d="M152.272 893.884C132.366 893.884 111.203 895.068 88.7821 897.438"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          d="M88.7821 934.305C106.018 933.012 123.696 932.366 141.816 932.366"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          opacity="0.8"
          d="M98.4149 1089.03H235.269"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          opacity="0.8"
          d="M98.4149 1050.55H235.269"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          opacity="0.8"
          d="M218.419 937.861H291.323"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          opacity="0.8"
          d="M84.4679 896.633L295.178 896.633"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          d="M154.468 1065.33V932.365"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          d="M118.787 1097.27L118.787 1038.07"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          d="M203.874 1097.27L203.874 887.751"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          d="M96.8287 946.107L96.8287 886.913"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          d="M277.971 946.107L277.971 886.913"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          d="M228.566 947.281L228.566 888.087"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <ellipse
          rx="12.652"
          ry="12.6698"
          transform="matrix(1 0 0 -1 141.816 945.035)"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></ellipse>
        <path
          d="M203.874 946.107C203.874 928.15 199.665 914.956 191.248 906.527C182.831 898.098 169.839 893.884 152.273 893.884M152.272 893.884C146.291 893.884 140.356 894.936 134.732 896.993M203.874 946.108C203.874 953.226 202.436 960.269 199.648 966.805"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <ellipse
          rx="24.6877"
          ry="24.7222"
          transform="matrix(1 0 0 -1 171.424 922.559)"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></ellipse>
        <path
          d="M123.051 959.67L210.676 891.327"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.12319"
        ></path>
        <path
          d="M5.58532 1174L384.446 1174M17 694.001L361.4 694.001M38 647.8L38 1206.4M338 662.201L338 1195"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.5"
        ></path>
        <path
          d="M5.60003 1175.2L384.461 1175.2M17.0147 695.2L361.414 695.2M38.0147 649L38.0147 1207.6M338.014 663.4L338.014 1196.2"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="1.5"
        ></path>
      </g>
    </g>
    <defs>
      <filter
        id="filter0_d"
        x="96.8165"
        y="153.778"
        width="109.803"
        height="196.478"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
        ></feColorMatrix>
        <feOffset></feOffset>
        <feGaussianBlur stdDeviation="0.280797"></feGaussianBlur>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0.611765 0 0 0 0 0.937255 0 0 0 1 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter1_d"
        x="228.029"
        y="305.626"
        width="50.3565"
        height="42.1965"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
        ></feColorMatrix>
        <feOffset></feOffset>
        <feGaussianBlur stdDeviation="0.280797"></feGaussianBlur>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0.611765 0 0 0 0 0.937255 0 0 0 1 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter2_d"
        x="95.1441"
        y="153.281"
        width="184.512"
        height="198.519"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
        ></feColorMatrix>
        <feOffset></feOffset>
        <feGaussianBlur stdDeviation="0.467995"></feGaussianBlur>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0.611765 0 0 0 0 0.937255 0 0 0 1 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter3_f"
        x="-4.40002"
        y="639"
        width="398.861"
        height="578.6"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="BackgroundImageFix"
          result="shape"
        ></feBlend>
        <feGaussianBlur
          stdDeviation="5"
          result="effect1_foregroundBlur"
        ></feGaussianBlur>
      </filter>
      <filter
        id="filter4_d"
        x="96.8165"
        y="893.743"
        width="109.803"
        height="196.478"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
        ></feColorMatrix>
        <feOffset></feOffset>
        <feGaussianBlur stdDeviation="0.280797"></feGaussianBlur>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0.611765 0 0 0 0 0.937255 0 0 0 1 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter5_d"
        x="228.029"
        y="896.177"
        width="50.3565"
        height="42.1965"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
        ></feColorMatrix>
        <feOffset></feOffset>
        <feGaussianBlur stdDeviation="0.280797"></feGaussianBlur>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0.611765 0 0 0 0 0.937255 0 0 0 1 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter6_d"
        x="95.1441"
        y="892.201"
        width="184.512"
        height="198.519"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
        ></feColorMatrix>
        <feOffset></feOffset>
        <feGaussianBlur stdDeviation="0.467995"></feGaussianBlur>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0.611765 0 0 0 0 0.937255 0 0 0 1 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        ></feBlend>
      </filter>
      <linearGradient
        id="paint0_linear"
        x1="-334.2"
        y1="787.2"
        x2="462.6"
        y2="-214.2"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" stopOpacity="0.2"></stop>
        <stop offset="1" stopColor="white" stopOpacity="0"></stop>
      </linearGradient>
      <linearGradient
        id="paint1_linear"
        x1="-37.375"
        y1="609.25"
        x2="-37.3751"
        y2="1058.13"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopOpacity="0.7"></stop>
        <stop offset="1" stopOpacity="0.01"></stop>
      </linearGradient>
      <linearGradient
        id="paint2_linear"
        x1="-334.2"
        y1="787.2"
        x2="462.6"
        y2="-214.2"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" stopOpacity="0.2"></stop>
        <stop offset="1" stopColor="white" stopOpacity="0"></stop>
      </linearGradient>
      <clipPath id="clip0">
        <rect width="375" height="1209" fill="white"></rect>
      </clipPath>
    </defs>
  </svg>
)
